import Callout from "components/callout/Callout";
import { CustomerTeeTimeCard } from "components/customerTeeTimeCard/CustomerTeeTimeCard";
import LoaderCard from "components/loaderCard/LoaderCard";
import Radio from "components/radio";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import React, { useEffect, useMemo, useState } from "react";
import "../clientPortalHome.scss";
import { cartOptions, holeOptions, playerOptions } from "../teeTimeFilters";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GetCustomerCheckIns, ICheckIns, INotification } from "api/rpc/2024-04/customer/teesheet";
import { IFacility } from "redux/reducers/models/facility";
import axios, { CancelToken } from "axios";
import { IFacilityTeeSheetSettings } from "api/rpc/2024-04/customer/facility";
import { IAPIResponse, StatusCode } from "api/protocols";
import { showError } from "redux/actions/ui";
import moment from "moment";
import { ITeeTime } from "redux/reducers/models/teetime";
import { convertTime } from "helpers/Helpers";
import ReactDOM from "react-dom";
import { GetGuestCheckIns } from "api/rpc/2024-04/guest/teesheet";
import { IFilterState } from "../ClientPortalHome";

interface IProps {
  checkTeeTime: (date: string, teeTime: ITeeTime) => Promise<void>;
  dayOfWeekNumber: number;
  filterState: Partial<IFilterState>;
  setFilterState: React.Dispatch<React.SetStateAction<IFilterState>>;
  teeTimeRes: IAPIResponse<Array<ITeeTime>>;
  notifications: Array<INotification>;
}
interface ITeeTimeCard {
  id: number;
  convertedTime: string;
  time: string;
  prepaid: boolean;
  quantity_remaining: number;
  holes: number[];
  cart_rule: string;
  nine_hole_price: number;
  eighteen_hole_price: number;
  courseName?: string;
  date: string;
  position: number;
  divisionTitle?: string;
  blocked_type?: unknown;
  startingHole?: string;
  leagueId: number;
  course_id: number;
  teeTime: ITeeTime;
}

interface IState {
  teeTimeCards: Array<ITeeTimeCard>;
  checkInRes: IAPIResponse<Array<ICheckIns>>;
}

interface ILeagueState {
  leagues: Array<{ id: string | number; name: string }>;
  selectedLeagueId: string | number;
}

export default function ClientPortalTeeTimesList(props: IProps) {
  const { checkTeeTime, dayOfWeekNumber, filterState, setFilterState, teeTimeRes, notifications } = props;
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { clientPortalStore } = useAppSelector(store => store);
  const [state, setState] = useState<IState>({
    teeTimeCards: undefined,
    checkInRes: undefined,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (teeTimeRes && clientPortalStore?.selectedFacility && clientPortalStore?.teeSheetSettings) {
      const teeTimeCards = setTeeTimeCards(
        teeTimeRes,
        undefined,
        clientPortalStore?.selectedFacility,
        clientPortalStore?.teeSheetSettings,
      );
      ReactDOM.unstable_batchedUpdates(() => {
        setFilterState(prevState => ({
          ...prevState,
          enable_cart_bookings: !!clientPortalStore?.teeSheetSettings?.enable_cart_bookings,
        }));
        setState(prevState => ({ ...prevState, teeTimeCards }));
      });

      // Load check ins for pricing in the background
      void loadCheckIns(
        !!clientPortalStore?.user,
        source.token,
        teeTimeRes,
        clientPortalStore?.selectedFacility,
        clientPortalStore?.teeSheetSettings,
      );
    } else {
      setState(prevState => ({ ...prevState, teeTimeCards: undefined }));
    }
    return () => source.cancel();
  }, [teeTimeRes]);

  useEffect(() => {
    //Update pricing display based on holes filter selection
    if (state?.teeTimeCards !== undefined && teeTimeRes && state?.checkInRes) {
      const teeTimeCards = setTeeTimeCards(
        teeTimeRes,
        state?.checkInRes,
        clientPortalStore?.selectedFacility,
        clientPortalStore?.teeSheetSettings,
      );
      setState(prevState => ({ ...prevState, teeTimeCards: teeTimeCards }));
    }
  }, [filterState.selectedHoles]);

  async function loadCheckIns(
    userActive: boolean,
    cancelToken: CancelToken,
    teeTimesRes: IAPIResponse<Array<ITeeTime>>,
    facility: Partial<IFacility>,
    facilityTeeSheetSettings: IFacilityTeeSheetSettings,
  ) {
    let checkInConfigRes: IAPIResponse<ICheckIns[]> = null;
    if (facilityTeeSheetSettings?.display_online_pricing) {
      if (userActive) {
        //User logged in
        checkInConfigRes = await GetCustomerCheckIns(
          { facility_id: facility?.id, day_of_week_number: dayOfWeekNumber },
          false,
          cancelToken,
        );
        if (checkInConfigRes.status !== StatusCode.OK) {
          if (cancelToken && cancelToken?.reason) {
            return;
          }
          dispatch(showError(t("Error getting pricing"))); // TODO: Translation
        }
      } else {
        //Guest
        checkInConfigRes = await GetGuestCheckIns(
          { facility_id: facility?.id, day_of_week_number: dayOfWeekNumber },
          false,
          cancelToken,
        );
        if (checkInConfigRes.status !== StatusCode.OK) {
          if (cancelToken && cancelToken?.reason) {
            return;
          }
          dispatch(showError(t("Error getting pricing"))); // TODO: Translation
        }
      }
    }

    const teeTimeCards = setTeeTimeCards(teeTimesRes, checkInConfigRes, facility, facilityTeeSheetSettings);
    setState(prevState => ({ ...prevState, teeTimeCards, checkInRes: checkInConfigRes }));
  }

  function filterTeeTimeCards(teeTimeCards: ITeeTimeCard[]) {
    return teeTimeCards.filter(teeTimeCard => {
      if (
        clientPortalStore?.teeSheetSettings?.single_bookings === "allow_within_group" &&
        parseInt(filterState?.selectedPlayers) === 1
      ) {
        return teeTimeCard.quantity_remaining < 4;
      } else {
        return (
          (filterState.selectedPlayers === null ||
            teeTimeCard.quantity_remaining >= parseInt(filterState.selectedPlayers)) &&
          (filterState.selectedHoles === null || teeTimeCard.holes.includes(parseInt(filterState.selectedHoles))) &&
          (filterState.selectedCart === null ||
            ((teeTimeCard.cart_rule === "none" || teeTimeCard.cart_rule === "optional") &&
              filterState.selectedCart === "no") ||
            ((teeTimeCard.cart_rule === "required" || teeTimeCard.cart_rule === "optional") &&
              filterState.selectedCart === "yes")) &&
          (clientPortalStore?.teeSheetSettings?.single_bookings === "allow" ||
            clientPortalStore?.teeSheetSettings?.single_bookings === "allow_within_group" ||
            (clientPortalStore?.teeSheetSettings?.single_bookings === "none" && teeTimeCard?.quantity_remaining >= 2))
        );
      }
    });
  }

  function checkHolesSelected(holes: number) {
    switch (filterState.selectedHoles) {
      case "18":
        if (holes === 18) {
          return true;
        }
        break;
      case "9":
        if (holes === 9) {
          return true;
        }
        break;
      case "any":
      case null:
        return true;
    }
    return false;
  }

  function invalidTeeTime(teeTime: ITeeTime) {
    return (
      teeTime?.divisions_available?.length === 1 && teeTime?.divisions_available[0] === 2 && !teeTime?.turn_tee_time_id
    );
  }

  function setTeeTimeCards(
    teeTimesRes: IAPIResponse<Array<ITeeTime>>,
    checkInRes: IAPIResponse<Array<ICheckIns>>,
    facility: Partial<IFacility>,
    facilityTeeSheetSettings: IFacilityTeeSheetSettings,
  ) {
    const teeTimes = teeTimesRes?.data ? [...teeTimesRes?.data] : [];
    const teetimeCards: ITeeTimeCard[] = [];
    const checkInsData = checkInRes?.data ? [...checkInRes?.data] : checkInRes === undefined ? undefined : [];
    if (teeTimes) {
      for (let i = 0; i < teeTimes?.length; i++) {
        if (invalidTeeTime(teeTimes[i])) {
          continue;
        }
        const teetimeData = teeTimes[i];
        const startTime = convertTime(teetimeData.start_time);

        const holes = teetimeData.divisions_available.map(divisionAvailable => {
          if (divisionAvailable === 1) {
            return 9;
          } else if (divisionAvailable === 2) {
            return 18;
          }
        });

        let eighteenHolePrice: number;
        let nineHolePrice: number;
        if (checkInsData) {
          checkInsData?.every(checkIn => {
            if (eighteenHolePrice && nineHolePrice) {
              return false;
            } else {
              const startTime = moment(checkIn?.pricing_sheet?.start_time, "HH:mm");
              const endTime = moment(checkIn?.pricing_sheet?.end_time, "HH:mm");
              const checkInCourseId = checkIn?.pricing_sheet?.course_id;
              if (
                (moment(teetimeData?.start_time, "HH:mm").isBetween(startTime, endTime) ||
                  moment(teetimeData?.start_time, "HH:mm").isSame(startTime) ||
                  moment(teetimeData?.start_time, "HH:mm").isSame(endTime)) &&
                checkHolesSelected(checkIn?.holes) &&
                checkInCourseId === teetimeData?.course_id
              ) {
                if (checkIn?.holes === 18 && holes?.includes(18) && !eighteenHolePrice) {
                  const priceOverride = checkIn?.variant?.inventory_levels?.find(
                    level => level?.facility_id === facility?.id,
                  )?.price_override;
                  eighteenHolePrice = priceOverride ?? checkIn?.variant?.price;
                } else if (checkIn?.holes === 9 && holes?.includes(9) && !nineHolePrice) {
                  const priceOverride = checkIn?.variant?.inventory_levels?.find(
                    level => level?.facility_id === facility?.id,
                  )?.price_override;
                  nineHolePrice = priceOverride ?? checkIn?.variant?.price;
                }
              }
              return true;
            }
          });
        }

        if (teetimeData.quantity_remaining >= 1) {
          const teetimeCard: ITeeTimeCard = {
            id: teetimeData.id,
            convertedTime: startTime,
            time: teetimeData.start_time,
            prepaid: teeTimes[i].pre_paid_required,
            quantity_remaining: teetimeData.quantity_remaining,
            holes: holes,
            cart_rule: teetimeData.cart_rule,
            eighteen_hole_price:
              eighteenHolePrice && !!facilityTeeSheetSettings?.display_online_pricing
                ? eighteenHolePrice
                : checkInsData === undefined
                ? undefined
                : null,
            nine_hole_price:
              nineHolePrice && !!facilityTeeSheetSettings?.display_online_pricing
                ? nineHolePrice
                : checkInsData === undefined
                ? undefined
                : null,
            courseName: facility?.courses?.length > 1 ? teetimeData.course_full_name : undefined,
            date: teetimeData.date,
            position: teetimeData.position,
            divisionTitle: teetimeData.division_title,
            blocked_type: teetimeData.blocked_type,
            startingHole: teetimeData.start_hole,
            leagueId: teetimeData.league_id,
            course_id: teetimeData?.course_id,
            teeTime: teetimeData,
          };

          teetimeCards.push(teetimeCard);
        }
      }
      return teetimeCards;
    }
  }

  const teeTimeCards = useMemo(() => {
    return state?.teeTimeCards !== undefined
      ? state?.teeTimeCards
        ? filterTeeTimeCards(state?.teeTimeCards)
        : []
      : undefined;
  }, [state?.teeTimeCards, filterState.selectedCart, filterState.selectedPlayers]);

  return (
    <>
      {/* Desktop filters */}
      <div className="client-portal-home-filter-section">
        <div className="client-portal-home-filters">
          <div>
            <div className="client-portal-home-filter-header">{"Number of Players"}</div>
            <Radio.Group
              name="players"
              onChange={(players: string) =>
                setFilterState(prevState => ({ ...prevState, selectedPlayers: players === "any" ? null : players }))
              }
              value={filterState.selectedPlayers ?? "any"}
            >
              {playerOptions(t).map(player => {
                return (
                  <Radio.Button key={`players_${player.value}`} value={player.value} disabled={player?.disabled}>
                    {player.title}
                  </Radio.Button>
                );
              })}
            </Radio.Group>
          </div>
          <div>
            <div className="client-portal-home-filter-header">{"Holes"}</div>
            <Radio.Group
              name="holes"
              onChange={(holes: string) =>
                setFilterState(prevState => ({ ...prevState, selectedHoles: holes === "any" ? null : holes }))
              }
              value={filterState.selectedHoles ?? "any"}
            >
              {holeOptions.map(hole => {
                return (
                  <Radio.Button key={`holes_${hole.value}`} value={hole.value}>
                    <span>{hole.title}</span>
                    {hole.value !== "any" ? (
                      <FontAwesomeIcon
                        className="client-portal-home-filter-golf-icon"
                        icon={["far", "golf-flag-hole"]}
                      />
                    ) : null}
                  </Radio.Button>
                );
              })}
            </Radio.Group>
          </div>

          {filterState?.enable_cart_bookings ? (
            <div>
              <div className="client-portal-home-filter-header">{"Cart"}</div>
              <Radio.Group
                name="carts"
                onChange={(carts: string) =>
                  setFilterState(prevState => ({ ...prevState, selectedCart: carts === "any" ? null : carts }))
                }
                value={filterState.selectedCart ?? "any"}
              >
                {cartOptions.map(cartOption => {
                  return (
                    <Radio.Button key={`cart_${cartOption.value}`} value={cartOption.value}>
                      {cartOption.title}
                    </Radio.Button>
                  );
                })}
              </Radio.Group>
            </div>
          ) : null}
        </div>
      </div>
      <div className="client-portal-home-tee-time-section">
        <div>
          {notifications.map((notification, index) => {
            return <Callout key={index} type="info" title={notification.subject} content={notification.content} />;
          })}

          {state?.teeTimeCards?.length === 0 && (
            <Callout type="error" title={"No Tee Times"} content={t("There are no tee times for this day")} /> // TODO: Translation
          )}
          {state?.teeTimeCards && state?.teeTimeCards?.length !== 0 && teeTimeCards?.length === 0 && (
            <Callout
              type="error"
              title={t("No Tee Times")} // TODO: Translation
              content={t("There are no tee times that match the selected filters")} // TODO: Translation
            />
          )}
        </div>
        <div className="client-portal-home-tee-times">
          {teeTimeCards === undefined
            ? [...Array(3)]?.map((num, index) => {
                return (
                  <LoaderCard
                    key={index}
                    textRows={4}
                    customCSSProperties={{ width: "100%", height: "190px" }}
                    textRowSpacing={25}
                  />
                );
              })
            : teeTimeCards?.map((teetime, index) => {
                let courseName;

                if (clientPortalStore?.selectedFacility?.courses.length > 1) {
                  courseName = "Course: " + teetime.courseName;
                }

                return (
                  <div key={index}>
                    <CustomerTeeTimeCard
                      time={teetime.convertedTime}
                      isPrepaid={teetime.prepaid}
                      isShotgun={teetime.blocked_type === "shotgun"}
                      spotsRemaining={teetime.quantity_remaining}
                      holes={teetime.holes}
                      cartRule={filterState?.enable_cart_bookings ? teetime.cart_rule : "none"}
                      eighteenHolePrice={teetime.eighteen_hole_price}
                      nineHolePrice={teetime.nine_hole_price}
                      onClick={() => checkTeeTime(teetime.date, teetime?.teeTime)}
                      courseName={clientPortalStore?.selectedFacility?.courses?.length > 1 ? courseName : null}
                      divisionTitle={teetime.divisionTitle}
                      singleBookings={clientPortalStore?.teeSheetSettings?.single_bookings}
                      startingHole={teetime.startingHole}
                      isLeague={!!teetime.leagueId}
                    />
                  </div>
                );
              })}
        </div>
      </div>
    </>
  );
}
